import { Component, ElementRef, effect, inject } from '@angular/core';
import { TranslatePipe, TranslateDirective, TranslateService } from '@ngx-translate/core';
import { DownloadEntry, DownloadService, DownloadState, isTerminal } from '../../core/download.service';

//
// Replaces the .progressDownload modal and the download half of the spinner overlay.
//
// A list rather than a modal, and non-blocking rather than an overlay, because the shape of the old
// feedback assumed there was at most one download: an overlay cannot represent two, and it swallowed
// clicks for two seconds so the second download could not even be started (design F-3, P-6, P-8).
// Stacked modals are no alternative - three of them do not fit the 1024 by 768 that UI-9 requires.
//
// Rendered from app.html, outside app-parameter-form, so a download survives the user moving to
// another report. That is the scenario this exists for and no component inside the form can do it.
//
@Component({
  selector: 'app-download-list',
  standalone: true,
  imports: [TranslatePipe, TranslateDirective],
  templateUrl: './download-list.component.html',
  styles: [`
    :host { display: block; }

    /* In the layout flow rather than pinned over the content. A region floating above the page is the
       textbook SC 2.4.11 Focus Not Obscured failure: a keyboard user tabs to a control underneath it
       and lands behind it (design S-26). */
    .downloadList { margin: 16px 0 0; border: 1px solid #d5d5d5; border-radius: 4px; background: #fff; }
    .downloadListHeading { display: flex; align-items: center; gap: 8px; margin: 0; padding: 8px 12px; border-bottom: 1px solid #e5e5e5; font-size: 14px; font-weight: 600; }
    .downloadCount { color: #666; font-weight: 400; }
    .downloadClear { margin-left: auto; }

    .downloadNotice { display: flex; align-items: center; gap: 8px; margin: 0; padding: 8px 12px; border-bottom: 1px solid #e5e5e5; background: #fff7e0; color: #5a4500; font-size: 13px; }
    .downloadNoticeDismiss { margin-left: auto; }

    .downloadRows { list-style: none; margin: 0; padding: 0; }
    .downloadRow { padding: 10px 12px; border-top: 1px solid #f0f0f0; }
    .downloadRow.highlighted { background: #fff7e0; }
    .downloadRow:first-child { border-top: 0; }
    .downloadRowHead { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .downloadTitle { font-weight: 600; }
    .downloadState { color: #555; }

    /* SC 2.5.8 Target Size (Minimum), new in WCAG 2.2: 24 by 24 CSS pixels is the floor, and a row of
       small icon-only controls is the usual way to fail it. */
    .downloadAction { min-width: 24px; min-height: 24px; padding: 2px 8px; border: 1px solid #c5c5c5; border-radius: 3px; background: #f7f7f7; color: #222; cursor: pointer; font: inherit; font-size: 12px; }
    .downloadAction:hover { background: #eee; }
    .downloadDismiss { margin-left: auto; font-size: 16px; line-height: 1; }

    .downloadBar { position: relative; height: 6px; margin: 8px 0 4px; border-radius: 3px; background: #e3e3e3; overflow: hidden; }
    .downloadBarFill { height: 100%; background: #67a5db; transition: width 0.2s linear; }
    .downloadBarFill.indeterminate { width: 35%; animation: app-download-slide 1.4s ease-in-out infinite; }
    @keyframes app-download-slide { 0% { margin-left: -35%; } 100% { margin-left: 100%; } }

    .downloadHint { margin: 2px 0; color: #555; font-size: 12px; }
    .downloadNote { margin: 2px 0; padding: 4px 6px; border-left: 3px solid #c9a227; background: #fff7e0; color: #5a4500; font-size: 12px; }
    .downloadRequest { margin: 2px 0 0; color: #666; font-size: 11px; word-break: break-all; }
    .downloadRequestLabel { font-weight: 600; }

    .visuallyHidden { position: absolute; width: 1px; height: 1px; margin: -1px; padding: 0; overflow: hidden; clip-path: inset(50%); white-space: nowrap; border: 0; }

    /* Matches the dark treatment menu.css already gives .errorOverlay and the spinner. */
    @media (prefers-color-scheme: dark) {
      .downloadList { border-color: #4a4a4a; background: #2a2a2a; color: #e8e8e8; }
      .downloadListHeading { border-bottom-color: #3d3d3d; }
      .downloadRow { border-top-color: #3d3d3d; }
      .downloadState, .downloadHint, .downloadRequest, .downloadCount { color: #b9b9b9; }
      .downloadAction { border-color: #555; background: #3a3a3a; color: #e8e8e8; }
      .downloadAction:hover { background: #454545; }
      .downloadBar { background: #444; }
      .downloadNotice { background: #4a3f1c; color: #ffe9a8; border-bottom-color: #3d3d3d; }
      .downloadNote { border-left-color: #c9a227; background: #4a3f1c; color: #ffe9a8; }
      .downloadRow.highlighted { background: #3a3320; }
    }

    /* 320 px: the request summary is long, so the row stacks rather than pushing controls off-screen
       (UI-9). It stays present - it is what tells two runs of one report apart. */
    @media (max-width: 480px) {
      .downloadRowHead { gap: 6px; }
      .downloadTitle { flex-basis: 100%; }
    }
  `],
})
export class DownloadListComponent
{
  protected readonly downloads = inject(DownloadService);
  private readonly translate = inject(TranslateService);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);

  //
  // Bring the list into view when a download is added or a submit is refused.
  //
  // Not decoration. The list sits in the layout flow above the report, and a report with ten
  // parameters puts its Download button some 1700 px below it - so pressing Download added a row the
  // user could not see, on a page that otherwise did not change, which reads exactly like a button
  // that does nothing. The modal this replaced was centred on the screen and had no such problem.
  //
  // Scrolls, and never focuses. Moving focus on a state change would interrupt a user typing in the
  // next report and is what SC 2.4.3 and the design's own S-23 rule out; scrolling the region into
  // view carries none of that, and `block: 'nearest'` moves as little as possible.
  //
  constructor()
  {
    effect(() =>
    {
      this.downloads.revealCounter();

      //
      // After the render that the signal above is about to trigger, not before it: the row does not
      // exist yet at the moment the effect runs.
      //
      setTimeout(() =>
      {
        const list = this.host.nativeElement.querySelector('.downloadList');
        if (!list) return;

        const box = list.getBoundingClientRect();
        const visible = box.bottom > 0 && box.top < (window.innerHeight || document.documentElement.clientHeight);
        if (!visible)
        {
          list.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }, 0);
    });
  }

  protected isTerminalState(state: DownloadState): boolean
  {
    return isTerminal(state);
  }

  protected canCancel(row: DownloadEntry): boolean
  {
    //
    // Only the fetch route can be abandoned. A native submit belongs to the browser the moment it is
    // made, and offering a control that does nothing would be worse than offering none.
    //
    return row.route === 'fetch' && !isTerminal(row.state);
  }

  //
  // A detail is offered only where there is one: a server answer that was not a file. A request that
  // never completed has no status, no headers and no body, so a modal about it can only repeat the
  // row - and the first build repeated it wrongly, as "HTTP 0, body is not JSON".
  //
  protected canShowDetails(row: DownloadEntry): boolean
  {
    return row.state === 'failed' && row.failure?.kind === 'answer';
  }

  protected stateText(row: DownloadEntry): string
  {
    return this.translate.instant('itgen_gmr_download_state_' + row.state);
  }

  protected hintText(row: DownloadEntry): string
  {
    return this.translate.instant('itgen_gmr_download_hint_' + row.state);
  }

  //
  // The accessible name of a row's progress bar carries the report's own name, because a list of
  // three bars all called "Downloading" tells a screen-reader user nothing about which is which -
  // exactly the collection-naming failure UI-3 exists to prevent.
  //
  protected progressLabel(row: DownloadEntry): string
  {
    return this.translate.instant(row.titleCode) + ' — ' + this.stateText(row);
  }

  protected progressText(row: DownloadEntry): string
  {
    if (!row.totalBytes) return this.stateText(row);
    return formatBytes(row.receivedBytes) + ' / ' + formatBytes(row.totalBytes);
  }

  protected percentage(row: DownloadEntry): number | null
  {
    if (!row.totalBytes) return null;
    return Math.min(100, Math.round((row.receivedBytes / row.totalBytes) * 100));
  }

  protected cancelLabel(row: DownloadEntry): string
  {
    return this.translate.instant('itgen_gmr_download_cancel_report', { report: this.translate.instant(row.titleCode) });
  }

  protected detailsLabel(row: DownloadEntry): string
  {
    return this.translate.instant('itgen_gmr_download_details_report', { report: this.translate.instant(row.titleCode) });
  }

  protected dismissLabel(row: DownloadEntry): string
  {
    return this.translate.instant('itgen_gmr_download_dismiss_report', { report: this.translate.instant(row.titleCode) });
  }
}

function formatBytes(bytes: number): string
{
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' kB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}
